// Spor A block 3 (XDIS-EPJ 5.4 / 7.8): the receiver of an XDIS91 answer or an XDIS18
// udskrivningsrapport is choosable. Default: the received letter's sender (XDIS91) or the
// XDIS16's sender (XDIS18). Alternatives: MedCom Testcenter (protocol EAN), a unit from SOR,
// or a typed EAN with identifier and identifier code. The EAN goes into Receiver/EANIdentifier.

import type { Party } from './xdis18Form';

/** MedCom Testcenter, XDIS-EPJ step 5.4 (XDIS91 answer). */
export const XDIS91_TESTCENTER_EAN = '5790000120314';
/** MedCom Testcenter, XDIS-EPJ step 7.8 (XDIS18). */
export const XDIS18_TESTCENTER_EAN = '5790001382445';

export const XDIS_IDENTIFIER_CODES = ['lokationsnummer', 'kommunenummer', 'sorkode', 'sygehusafdelingsnummer', 'ydernummer'] as const;

export type ReceiverMode = 'default' | 'testcenter' | 'sor' | 'manual';
export type ReceiverChoice = { mode: ReceiverMode; party: Party | null };

export const RECEIVER_LIMITS = { identifier: 17, organisationName: 35, departmentName: 35, unitName: 35 } as const;

/** The testcenter as receiver: its EAN is also the identifier, as lokationsnummer. */
export function testcenterParty(ean: string): Party {
  return { ean, identifier: ean, identifierCode: 'lokationsnummer', organisationName: 'MedCom Testcenter' };
}

export function emptyParty(): Party {
  return { ean: '', identifier: '', identifierCode: 'lokationsnummer' };
}

/** Problems with a chosen receiver (Danish, for the form). Empty when it can be built. */
export function receiverIssues(party: Party | null): string[] {
  if (!party) return ['Vælg en modtager'];
  const out: string[] = [];
  if (!/^\d{13}$/.test(party.ean.trim())) out.push('Modtagerens lokationsnummer (EAN) skal være 13 cifre');
  if (!party.identifier.trim()) out.push('Modtagerens identifikator mangler');
  if (party.identifier.trim().length > RECEIVER_LIMITS.identifier) out.push(`Modtagerens identifikator må højst være ${RECEIVER_LIMITS.identifier} tegn`);
  if (!party.identifierCode.trim()) out.push('Modtagerens identifikatorkode mangler');
  for (const key of ['organisationName', 'departmentName', 'unitName'] as const) {
    if ((party[key] ?? '').length > RECEIVER_LIMITS[key]) out.push(`Modtagerens ${key} må højst være ${RECEIVER_LIMITS[key]} tegn`);
  }
  return out;
}

/** The receiver to send, trimmed; null for the default rule (or nothing picked yet). */
export function chosenReceiver(choice: ReceiverChoice, fallback: Party | null): Party | null {
  const party = choice.mode === 'default' ? fallback : choice.party;
  if (!party) return null;
  const trimmed: Party = { ...party, ean: party.ean.trim(), identifier: party.identifier.trim(), identifierCode: party.identifierCode.trim() };
  for (const key of Object.keys(trimmed) as (keyof Party)[]) {
    const v = trimmed[key];
    if (typeof v === 'string' && v.trim() === '' && key !== 'ean' && key !== 'identifier' && key !== 'identifierCode') delete trimmed[key];
  }
  return trimmed;
}

/** Party for a mode switch: testcenter preset, keep the current one for manual, nothing for SOR. */
export function partyForMode(mode: ReceiverMode, testcenterEan: string, current: Party | null): Party | null {
  if (mode === 'testcenter') return testcenterParty(testcenterEan);
  if (mode === 'manual') return current ? { ...current } : emptyParty();
  return null;
}
